import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "@repo/i18n";
import { layoutZones, scenePxPerUnit, TABLE_SIZE } from "../features/floor-plan/floorLayout";
import type { FloorPlanResponse } from "../features/floor-plan/floorApi";
import { cleaningElapsedMin, tableStateTint } from "./lateness";
import { mapStyles as m, tableTint } from "./styles";
import type { LiveRow, LiveTable } from "./types";

/** A booking armed for (re)seating: the next available table tapped on the map seats it there. */
export interface ArmedSeat {
  bookingId: string;
  version: number;
  label: string;
  /** Set when the arming came from picking up an already-seated table (a reassign, not a first seat). */
  fromTableId?: string;
}

/**
 * The live floor as the manager DREW it: the floor-plan geometry (table x/y, shapes, zones) painted with
 * the REAL per-table state from the live `/floor` read, joined by tableId. This is the operational
 * surface the flat tile grid never was — you see who's where, and you tap a table to seat/reassign.
 *
 * FIT (not balloon): the scene renders at a CLAMPED pixel scale (`scenePxPerUnit`) so a `TABLE_SIZE`
 * table stays ~44..64 CSS px whatever the viewport — a big/sparse room SCROLLS inside the pan
 * container rather than shrinking its tap targets, and a wide desktop stops the scene ballooning and
 * centres it. The zone-band geometry is unchanged; only how FloorMap sizes/positions the scene changed.
 *
 * Interaction is modal:
 *  - armed (a booking is waiting): AVAILABLE tables become seat targets; tap one to seat/reassign there.
 *  - idle: tap an OCCUPIED/RESERVED table to pick up its party for a reassign, or a CLEANING table to
 *    mark it ready. AVAILABLE tables are inert until a booking is armed.
 *
 * All state is server-derived (R1); this only renders + taps. The parent owns the seat/ready mutations
 * and the 409 "already updated" refetch, so no last-write-wins here.
 */
export function FloorMap({
  plan,
  tables,
  rows = [],
  armed,
  nowSec,
  timeZone,
  onSeatTable,
  onPickTable,
  onTableReady,
}: {
  plan: FloorPlanResponse;
  tables: LiveTable[];
  /** Today's timeline rows, joined by `backingBookingId` for the occupant party size + initial. */
  rows?: LiveRow[];
  armed: ArmedSeat | null;
  nowSec: number;
  timeZone?: string;
  onSeatTable: (tableId: string) => void;
  onPickTable: (table: LiveTable) => void;
  onTableReady: (tableId: string) => void;
}) {
  const { t } = useTranslation();
  const stateById = useMemo(() => new Map(tables.map((tb) => [tb.tableId, tb])), [tables]);
  const rowById = useMemo(() => new Map(rows.map((r) => [r.bookingId, r])), [rows]);
  const active = useMemo(() => plan.tables.filter((tb) => !tb.retired), [plan.tables]);
  const layout = useMemo(
    () =>
      layoutZones(
        plan.zones.map((z) => ({ id: z.id, name: z.name })),
        active,
      ),
    [plan.zones, active],
  );

  // Measure the pan container so the clamped scale can react to the real viewport (ResizeObserver
  // when available; a one-shot measure otherwise). jsdom has no ResizeObserver and reports width 0 —
  // the clamp floors that to the min scale, so tables are still >= 44px in tests.
  const panRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    const el = panRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pxPerUnit = scenePxPerUnit(containerWidth, layout.width);
  const px = (units: number) => `${units * pxPerUnit}px`;

  return (
    <div ref={panRef} aria-label={t("live.floor.map")} className={m.pan}>
      <div className={m.scene} style={{ width: px(layout.width), height: px(layout.height) }}>
        {layout.bands.map((band) => (
          <div
            key={band.id}
            aria-hidden="true"
            className={m.band}
            style={{
              left: px(band.left),
              top: px(band.top),
              width: px(band.width),
              height: px(band.height),
            }}
          >
            <span className={m.bandLabel}>{band.name}</span>
          </div>
        ))}

        {layout.placed.map(({ tb, x, y }) => {
          const live = stateById.get(tb.id);
          const state = live?.state ?? "AVAILABLE";
          const tint = tableStateTint(state);
          const round = tb.shape === "ROUND" || tb.shape === "OVAL";
          const isTarget = armed !== null && state === "AVAILABLE";
          const isSource = armed?.fromTableId === tb.id;
          // While a booking is armed, only AVAILABLE tables (the seat targets) stay interactive.
          const disabled = armed !== null ? !isTarget : state === "AVAILABLE";

          const onClick = () => {
            if (armed !== null) {
              if (isTarget) onSeatTable(tb.id);
              return;
            }
            if (state === "CLEANING") onTableReady(tb.id);
            else if (live?.backingBookingId) onPickTable(live);
          };

          const stateWord = t(`live.state.${tint}`);
          const label = isTarget
            ? t("live.floor.seatHere", { label: tb.label })
            : t("live.floor.tableState", { label: tb.label, state: stateWord });

          const occupant =
            (live?.backingBookingId ? rowById.get(live.backingBookingId) : undefined) ?? null;
          const showParty = tint === "occupied" || tint === "reserved";
          const sinceLabel =
            showParty && live !== undefined && live.sinceSec !== null
              ? formatClock(live.sinceSec, timeZone)
              : null;

          return (
            <button
              key={tb.id}
              type="button"
              aria-label={label}
              disabled={disabled}
              onClick={onClick}
              className={`${tableTint[tint]} ${m.table} ${isTarget ? m.target : ""} ${
                isSource ? m.source : ""
              }`}
              style={{
                left: px(x),
                top: px(y),
                width: px(TABLE_SIZE),
                height: px(TABLE_SIZE),
                borderRadius: round ? "9999px" : "6px",
              }}
            >
              <span className={m.tableLabel}>{tb.label}</span>
              {showParty && occupant ? (
                <span className={m.tableParty} aria-hidden="true">
                  {occupant.partySize}
                  {"·"}
                  {initialOf(occupant)}
                </span>
              ) : (
                <span className={m.tableCap} aria-hidden="true">
                  {live?.maxCovers ?? tb.maxCovers}
                </span>
              )}
              {sinceLabel && (
                <span className={m.tableSince} aria-hidden="true">
                  {sinceLabel}
                </span>
              )}
              {live?.stuck && (
                <span className={m.stuckDot} aria-hidden="true">
                  {cleaningElapsedMin(live.sinceSec, nowSec)}′
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function initialOf(row: LiveRow): string {
  const name = (row.guestGivenName ?? row.guestFamilyName ?? "").trim();
  return name ? name.charAt(0).toUpperCase() : "—";
}

function formatClock(sec: number, timeZone: string | undefined): string {
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    }).format(sec * 1000);
  } catch {
    return new Date(sec * 1000).toISOString().slice(11, 16);
  }
}
